import React, { useMemo, useState } from 'react';

// Room service only ever sees already-checked-in rooms (filtered upstream by
// isRoomServiceEligible), so there's no Vacant/Occupied distinction here like
// the restaurant TableSelector has — the meaningful split for this screen is
// whether the room already has a live room-service order or not.
const STATUS_CFG = {
  has_order: { headerBg: '#2563eb', border: '#93c5fd', label: 'Order In Progress' },
  no_order:  { headerBg: '#16a34a', border: '#86efac', label: 'No Order Yet' },
};

export default function RoomSelector({ rooms, roomOrders, onSelect, onViewOrder, onPrintOrder, onBack, isMobile }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('FLOOR'); // 'FLOOR' | 'LIST'

  // Map room_number → aggregated info from roomOrders
  const ordersByRoom = useMemo(() => {
    const map = {};
    for (const o of (roomOrders || [])) {
      const rn = String(o.room_number || '').trim();
      if (!rn) continue;
      if (!map[rn]) map[rn] = { total: 0, orders: [], guestName: o.guest_name || '' };
      map[rn].total += Number(o.grand_total || o.total_amount || o.subtotal || 0);
      map[rn].orders.push(o);
      if (!map[rn].guestName && o.guest_name) map[rn].guestName = o.guest_name;
    }
    return map;
  }, [roomOrders]);

  const enriched = useMemo(() => {
    return (rooms || []).map((room) => {
      const rn = String(room.room_number || '');
      const orderInfo = ordersByRoom[rn];
      const hasOrders = Boolean(orderInfo && orderInfo.total > 0);
      return {
        room,
        rn,
        status: hasOrders ? 'has_order' : 'no_order',
        guestName: room.guest_name || room.primary_guest_name || orderInfo?.guestName || '',
        pax: room.pax_count ?? room.total_pax ?? null,
        floor: String(room.floor_no ?? '').trim() || 'Unspecified Floor',
        roomTypeName: room.room_type_name || '',
        orderInfo,
      };
    });
  }, [rooms, ordersByRoom]);

  const statusCounts = useMemo(() => {
    const counts = { ALL: enriched.length, has_order: 0, no_order: 0 };
    enriched.forEach((r) => { counts[r.status] += 1; });
    return counts;
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return enriched.filter((r) => {
      if (statusFilter !== 'ALL' && r.status !== statusFilter) return false;
      if (!q) return true;
      return r.rn.toLowerCase().includes(q) || r.guestName.toLowerCase().includes(q);
    });
  }, [enriched, search, statusFilter]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const r of filtered) {
      if (!groups[r.floor]) groups[r.floor] = [];
      groups[r.floor].push(r);
    }
    return groups;
  }, [filtered]);

  const floors = Object.keys(grouped).sort((a, b) => {
    const na = Number(a), nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.localeCompare(b);
  });

  const btn = { borderRadius: 7, border: 'none', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer', padding: '7px 10px', lineHeight: 1.3 };
  const withOrderCount = statusCounts.has_order || 0;
  const withOrderPct = statusCounts.ALL ? Math.round((withOrderCount / statusCounts.ALL) * 100) : 0;

  const renderCard = (r) => {
    const cfg = STATUS_CFG[r.status];
    const hasOrders = r.status === 'has_order';
    return (
      <div key={r.room.id || r.rn} style={{ borderRadius: 12, border: `1px solid ${cfg.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 6px rgba(0,0,0,0.07)', background: '#fff' }}>
        {/* Coloured header band */}
        <div style={{ background: cfg.headerBg, padding: '10px 12px 9px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>Room {r.rn}</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.73rem', marginTop: 3 }}>{cfg.label}</div>
          </div>
          {r.pax != null && (
            <div style={{ background: 'rgba(255,255,255,0.22)', borderRadius: 5, padding: '2px 6px', display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', color: '#fff', fontWeight: 600 }}>
              &#128100; {r.pax}
            </div>
          )}
        </div>

        {/* Card body */}
        <div style={{ padding: '10px 12px 6px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {r.roomTypeName && (
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{r.roomTypeName}</div>
          )}
          {r.guestName && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem' }}>
              <span style={{ color: '#9ca3af' }}>Guest</span>
              <span style={{ fontWeight: 600, color: '#111827' }}>{r.guestName}</span>
            </div>
          )}
          {hasOrders ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 2 }}>
              <div>
                <div style={{ fontSize: '0.69rem', color: '#9ca3af', marginBottom: 1 }}>Order Total</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                  &#8377;{Number(r.orderInfo.total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 0', gap: 4 }}>
              <div style={{ fontSize: '1.4rem' }}>&#127869;</div>
              <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>Ready for a new order</div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ padding: '6px 10px 10px', borderTop: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {hasOrders && (
            <div style={{ display: 'flex', gap: 5 }}>
              <button onClick={(e) => { e.stopPropagation(); onViewOrder && onViewOrder(r.room); }} style={{ ...btn, flex: 1, background: '#fff', border: '1px solid #93c5fd', color: '#2563eb' }}>
                &#128065; View
              </button>
              <button onClick={(e) => { e.stopPropagation(); onPrintOrder && onPrintOrder(r.room); }} style={{ ...btn, flex: 1, background: '#fff', border: '1px solid #93c5fd', color: '#2563eb' }}>
                &#128424; Print
              </button>
            </div>
          )}
          <button onClick={() => onSelect(r.room)} style={{ ...btn, width: '100%', background: '#0d9488', color: '#fff', fontWeight: 700 }}>
            {hasOrders ? 'Add Order' : 'Place Order'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: isMobile ? '1rem' : '1.5rem 2rem' }}>
      <div onClick={onBack} style={{ cursor: 'pointer', color: '#555', marginBottom: '1rem', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: '1.1rem' }}>&larr;</span> Back
      </div>

      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>Room Service</h2>
      <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1.1rem' }}>Manage in-room orders and occupancy in real-time</p>

      {/* Search + status filter + view toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search rooms or guests..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 160, maxWidth: 300, padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.9rem', outline: 'none' }}
        />
        {[['ALL', 'All'], ['no_order', 'No Order'], ['has_order', 'Order In Progress']].map(([key, label]) => {
          const active = statusFilter === key;
          const color = key === 'has_order' ? '#2563eb' : key === 'no_order' ? '#16a34a' : '#334155';
          return (
            <button key={key} type="button" onClick={() => setStatusFilter(key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '0.4rem 0.75rem', borderRadius: 8,
                border: active ? `1.5px solid ${color}` : '1px solid #e5e7eb',
                background: active ? `${color}14` : '#fff', color: active ? color : '#6b7280',
                fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
              {label} ({statusCounts[key] || 0})
            </button>
          );
        })}
        <div style={{ display: 'flex', gap: 0, marginLeft: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          {[['FLOOR', '⊞ Floor View'], ['LIST', '☰ List View']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setViewMode(key)}
              style={{ padding: '0.4rem 0.75rem', border: 'none', background: viewMode === key ? '#0d9488' : '#fff', color: viewMode === key ? '#fff' : '#6b7280', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {[
          { label: 'Total Rooms', value: statusCounts.ALL, bg: '#eff6ff', color: '#1d4ed8' },
          { label: 'Order In Progress', value: withOrderCount, pct: `${withOrderPct}%`, bg: '#eff6ff', color: '#2563eb' },
          { label: 'No Order Yet', value: statusCounts.no_order || 0, pct: `${100 - withOrderPct}%`, bg: '#f0fdf4', color: '#16a34a' },
        ].map((s) => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '0.7rem 0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{s.label}</div>
            </div>
            {s.pct && <div style={{ fontSize: '0.8rem', fontWeight: 700, color: s.color }}>{s.pct}</div>}
          </div>
        ))}
      </div>

      {viewMode === 'FLOOR' ? (
        floors.map((floor) => (
          <div key={floor} style={{ marginBottom: '2rem' }}>
            <div style={{ fontSize: '0.74rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: '0.7rem' }}>
              {floor === 'Unspecified Floor' ? floor : `Floor ${floor}`} &middot; {grouped[floor].length} Room{grouped[floor].length !== 1 ? 's' : ''}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill,minmax(220px,1fr))', gap: '0.9rem' }}>
              {grouped[floor].map(renderCard)}
            </div>
          </div>
        ))
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 90px 120px 120px', gap: 8, padding: '0.6rem 0.9rem', background: '#f8fafc', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
            <span>Room</span><span>Guest</span><span>Pax</span><span>Status</span><span>Action</span>
          </div>
          {filtered.map((r) => {
            const cfg = STATUS_CFG[r.status];
            return (
              <div key={r.room.id || r.rn} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 90px 120px 120px', gap: 8, padding: '0.6rem 0.9rem', borderTop: '1px solid #f1f5f9', alignItems: 'center', fontSize: '0.83rem' }}>
                <span style={{ fontWeight: 700 }}>{r.rn}</span>
                <span style={{ color: r.guestName ? '#111827' : '#cbd5e1' }}>{r.guestName || '—'}</span>
                <span>{r.pax ?? '—'}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: cfg.headerBg, fontWeight: 600 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.headerBg }} />
                  {cfg.label}
                </span>
                <button onClick={() => onSelect(r.room)} style={{ ...btn, background: '#0d9488', color: '#fff' }}>
                  {r.status === 'has_order' ? 'Add Order' : 'Place Order'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', color: '#999', padding: '3rem 0' }}>No rooms found</div>
      )}
    </div>
  );
}
